import React, { useState } from 'react';
import { getSocket } from '../socket';

const emptyRow = (lineNumber) => ({
  id: Date.now() + Math.random(),
  lineNumber,
  startTime: '',
  duration: '',
  presenter: '',
  description: '',
  remoteInPerson: '',
  mainScreens: '',
  dsm1: '',
  dsm2: '',
  secondaryScreens: '',
  notes: '',
  micAssignments: '',
  audioCues: '',
  lightingCues: '',
  stageLogistics: '',
  isAnnouncement: false,
  announcementText: '',
  manualColor: null,
  slides: '',
  rowNotes: '',
  custom2: '',
  custom3: '',
  custom4: '',
});

const emptyAnnouncement = (lineNumber) => ({
  id: Date.now() + Math.random(),
  lineNumber,
  startTime: '',
  duration: '',
  presenter: '',
  description: '',
  remoteInPerson: '',
  mainScreens: '',
  dsm1: '',
  dsm2: '',
  secondaryScreens: '',
  micAssignments: '',
  audioCues: '',
  lightingCues: '',
  stageLogistics: '',
  isAnnouncement: true,
  announcementText: '',
  custom2: '',
  custom3: '',
  custom4: '',
});

function getRowColor(row, index) {
  if (row.manualColor) return row.manualColor;
  const desc = (row.description || '').toLowerCase();
  const remote = (row.remoteInPerson || '').toLowerCase();
  if (desc.includes('video') || desc.includes('playback') || desc.includes('pb pro')) {
    return '#fef08a';
  }
  if (desc.includes('break') || desc.includes('lunch') || desc.includes('meal')) {
    return '#bfdbfe';
  }
  if (desc.trim() === 'end') {
    return '#fecaca';
  }
  if (desc.includes('pre show') || desc.includes('pre-show') || desc.includes('preshow')) {
    return '#e9d5ff';
  }
  if (remote.includes('remote') || remote.includes('both')) {
    return '#d1fae5';
  }
  return index % 2 === 0 ? '#ffffff' : '#e8eaed';
}

function ComboCell({ value, options, onChange, onCellFocus, onCellBlur }) {
  const [inputValue, setInputValue] = useState('');
  const [showList, setShowList] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editValue, setEditValue] = useState('');
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const tags = React.useMemo(
    () => (value ? value.split('\n').filter(v => v.trim() !== '') : []),
    [value]
  );

  const filtered = options.filter(opt =>
    opt.toLowerCase().includes(inputValue.toLowerCase())
  );

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    const newTags = [...tags, trimmed];
    onChange(newTags.join('\n'));
    setInputValue('');
    setShowList(false);
    inputRef.current?.focus();
  };

  const removeTag = (tag) => {
    const newTags = tags.filter(t => t !== tag);
    onChange(newTags.join('\n'));
  };

  const startEditTag = (tag) => {
    setEditingTag(tag);
    setEditValue(tag);
    setShowList(false);
  };

  const saveEditTag = () => {
    const trimmed = editValue.trim();
    if (!trimmed) { setEditingTag(null); return; }
    const newTags = tags.map(t => (t === editingTag ? trimmed : t));
    onChange(newTags.join('\n'));
    setEditingTag(null);
    setEditValue('');
  };

  const [dropdownStyle, setDropdownStyle] = useState({});

  const openDropdown = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const isDropUp = spaceBelow < 200;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: Math.max(rect.width, 180),
        ...(isDropUp
          ? { bottom: window.innerHeight - rect.top + 2, top: 'auto' }
          : { top: rect.bottom + 2, bottom: 'auto' }
        ),
      });
    }
    setShowList(true);
  };

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowList(false);
        if (inputValue.trim()) {
          const trimmed = inputValue.trim();
          if (trimmed && !tags.includes(trimmed)) {
            const newTags = [...tags, trimmed];
            onChange(newTags.join('\n'));
          }
          setInputValue('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, tags, onChange]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={() => { inputRef.current?.focus(); openDropdown(); }}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '3px',
          padding: '6px 8px',
          minHeight: '32px',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          textAlign: 'center',
          margin: '0 auto',
          cursor: 'pointer'
        }}>
        {tags.map((tag, tagIdx) => {
          const isManual = !options.includes(tag);
          if (editingTag === tag) {
            return (
              <input
                key={`edit-${tagIdx}`}
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={saveEditTag}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); saveEditTag(); }
                  if (e.key === 'Escape') { setEditingTag(null); setEditValue(''); }
                }}
                onClick={e => e.stopPropagation()}
                style={{
                  fontSize: '11px',
                  padding: '1px 4px',
                  border: '1px solid #4299e1',
                  borderRadius: '3px',
                  outline: 'none',
                  width: '90px'
                }}
              />
            );
          }
          return (
            <span key={`${tag}-${tagIdx}`} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              backgroundColor: '#1a1a2e',
              color: 'white',
              borderRadius: '3px',
              padding: '1px 5px',
              fontSize: '11px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              maxWidth: '100%'
            }}>
              {tag}
              {isManual && (
                <button
                  className="tag-edit-btn"
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    startEditTag(tag);
                  }}
                  title="Edit tag"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#90cdf4',
                    cursor: 'pointer',
                    padding: '0',
                    fontSize: '9px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0
                  }}
                >
                  ✏️
                </button>
              )}
              <button
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeTag(tag);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#90cdf4',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '12px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0
                }}
              >
                ×
              </button>
            </span>
          );
        })}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            openDropdown();
          }}
          onFocus={() => { openDropdown(); if (onCellFocus) onCellFocus(); }}
          onBlur={() => { if (onCellBlur) onCellBlur(); }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (filtered.length === 1) {
                addTag(filtered[0]);
              } else if (inputValue.trim()) {
                addTag(inputValue);
              }
            }
            if (e.key === 'Escape') setShowList(false);
            if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
              removeTag(tags[tags.length - 1]);
            }
          }}
          placeholder={tags.length === 0 ? 'Type or select...' : ''}
          style={{
            border: 'none',
            outline: 'none',
            fontSize: '11px',
            background: 'transparent',
            color: '#1a1a2e',
            minWidth: '4px',
            width: tags.length === 0 ? 'auto' : '4px',
            flex: tags.length === 0 ? 1 : '0 0 4px',
            wordBreak: 'break-word'
          }}
        />
      </div>

      {showList && (inputValue.trim().length > 0 || options.length > 0) && (
        <div style={{
          ...dropdownStyle,
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          minWidth: '180px',
          maxHeight: '180px',
          overflowY: 'auto'
        }}>
          {filtered.length === 0 && inputValue.trim() && (
            <div
              onMouseDown={() => addTag(inputValue)}
              style={{
                padding: '7px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                color: '#4299e1',
                fontStyle: 'italic',
                borderBottom: '1px solid #f7fafc'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ebf8ff'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
            >
              + Add "{inputValue}"
            </div>
          )}
          {filtered.map(opt => (
            <div
              key={opt}
              onMouseDown={() => addTag(opt)}
              style={{
                padding: '7px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                color: '#1a1a2e',
                borderBottom: '1px solid #f7fafc'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ebf8ff'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableCell({ value, onChange, type = 'text', options = [], style = {}, onCellFocus, onCellBlur, remoteEditor }) {
  const [localValue, setLocalValue] = useState(value);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  if (type === 'dropdown') {
    return (
      <td style={{
        padding: '4px',
        border: '1px solid #e2e8f0',
        minWidth: '100px',
        position: 'relative',
        textAlign: 'center',
        verticalAlign: 'middle',
        ...style,
        ...(remoteEditor ? { outline: `2px solid ${remoteEditor.color}`, outlineOffset: '-2px' } : {})
      }}>
        {remoteEditor && (
          <span style={{
            position: 'absolute',
            top: '-9px',
            left: '2px',
            backgroundColor: remoteEditor.color,
            color: 'white',
            fontSize: '9px',
            fontWeight: '700',
            padding: '0px 5px',
            borderRadius: '3px',
            whiteSpace: 'nowrap',
            zIndex: 5,
            pointerEvents: 'none'
          }}>
            {remoteEditor.name}
          </span>
        )}
        <ComboCell value={value} options={options} onChange={onChange} onCellFocus={onCellFocus} onCellBlur={onCellBlur} />
      </td>
    );
  }

  if (type === 'multiline') {
    return (
      <td style={{
        padding: '4px',
        border: '1px solid #e2e8f0',
        minWidth: '120px',
        position: 'relative',
        ...style,
        ...(remoteEditor ? { outline: `2px solid ${remoteEditor.color}`, outlineOffset: '-2px' } : {})
      }}>
        {remoteEditor && (
          <span style={{
            position: 'absolute',
            top: '-9px',
            left: '2px',
            backgroundColor: remoteEditor.color,
            color: 'white',
            fontSize: '9px',
            fontWeight: '700',
            padding: '0px 5px',
            borderRadius: '3px',
            whiteSpace: 'nowrap',
            zIndex: 5,
            pointerEvents: 'none'
          }}>
            {remoteEditor.name}
          </span>
        )}
        <textarea
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          onFocus={() => onCellFocus && onCellFocus()}
          onBlur={() => { onChange(localValue); if (onCellBlur) onCellBlur(); }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setLocalValue(value);
              e.target.blur();
            }
          }}
          rows={1}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '12px',
            outline: 'none',
            padding: '2px',
            color: '#1a1a2e',
            resize: 'none',
            fontFamily: 'inherit',
            lineHeight: '1.4',
            overflow: 'visible',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
          ref={el => {
            if (el) {
              el.style.height = 'auto';
              el.style.height = (el.scrollHeight + 4) + 'px';
            }
          }}
          onInput={e => {
            e.target.style.height = 'auto';
            e.target.style.height = (e.target.scrollHeight + 4) + 'px';
          }}
        />
        <span className="print-text-value" style={{ display: 'none' }}>{localValue}</span>
      </td>
    );
  }
if (type === 'nowrap') {
    return (
      <td style={{
        padding: '4px',
        border: '1px solid #e2e8f0',
        minWidth: '80px',
        position: 'relative',
        ...style,
        ...(remoteEditor ? { outline: `2px solid ${remoteEditor.color}`, outlineOffset: '-2px' } : {})
      }}>
        {remoteEditor && (
          <span style={{
            position: 'absolute',
            top: '-9px',
            left: '2px',
            backgroundColor: remoteEditor.color,
            color: 'white',
            fontSize: '9px',
            fontWeight: '700',
            padding: '0px 5px',
            borderRadius: '3px',
            whiteSpace: 'nowrap',
            zIndex: 5,
            pointerEvents: 'none'
          }}>
            {remoteEditor.name}
          </span>
        )}
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          onFocus={() => onCellFocus && onCellFocus()}
          onBlur={() => onCellBlur && onCellBlur()}
          onKeyDown={e => {
            if (e.key === 'Tab') {
              e.preventDefault();
              const currentTd = e.target.closest('td');
              const currentTr = currentTd.closest('tr');
              const allTds = Array.from(currentTr.querySelectorAll('td'));
              const currentIndex = allTds.indexOf(currentTd);
              const nextTd = allTds[currentIndex + 1];
              if (nextTd) {
                const nextInput = nextTd.querySelector('input, select, textarea');
                if (nextInput) {
                  setTimeout(() => { nextInput.focus(); }, 10);
                }
              }
            }
          }}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '12px',
            outline: 'none',
            padding: '2px',
            color: '#1a1a2e',
            whiteSpace: 'nowrap'
          }}
        />
      </td>
    );
  }

  if (type === 'number') {
    return (
      <td className="duration-col" align="center" style={{
        padding: '4px',
        border: '1px solid #e2e8f0',
        minWidth: '60px',
        textAlign: 'center',
        position: 'relative',
        ...style,
        ...(remoteEditor ? { outline: `2px solid ${remoteEditor.color}`, outlineOffset: '-2px' } : {})
      }}>
        {remoteEditor && (
          <span style={{
            position: 'absolute',
            top: '-9px',
            left: '2px',
            backgroundColor: remoteEditor.color,
            color: 'white',
            fontSize: '9px',
            fontWeight: '700',
            padding: '0px 5px',
            borderRadius: '3px',
            whiteSpace: 'nowrap',
            zIndex: 5,
            pointerEvents: 'none'
          }}>
            {remoteEditor.name}
          </span>
        )}
        <input
          className="duration-input"
          type="number"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          onFocus={() => onCellFocus && onCellFocus()}
          onBlur={() => onCellBlur && onCellBlur()}
          onKeyDown={e => {
            if (e.key === 'Tab') {
              e.preventDefault();
              const currentTd = e.target.closest('td');
              const currentTr = currentTd.closest('tr');
              const allTds = Array.from(currentTr.querySelectorAll('td'));
              const currentIndex = allTds.indexOf(currentTd);
              const nextTd = allTds[currentIndex + 1];
              if (nextTd) {
                const nextInput = nextTd.querySelector('input, select, textarea');
                if (nextInput) {
                  setTimeout(() => { nextInput.focus(); }, 10);
                }
              }
            }
          }}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '12px',
            outline: 'none',
            padding: '2px',
            color: '#1a1a2e',
            textAlign: 'center'
          }}
        />
        <span className="duration-print-value" style={{ display: 'none', textAlign: 'center', width: '100%', margin: '0 auto' }}>{value || ''}</span>
      </td>
    );
  }

  return (
    <td style={{
      padding: '4px',
      border: '1px solid #e2e8f0',
      minWidth: '80px',
      position: 'relative',
      ...style,
      ...(remoteEditor ? { outline: `2px solid ${remoteEditor.color}`, outlineOffset: '-2px' } : {})
    }}>
      {remoteEditor && (
        <span style={{
          position: 'absolute',
          top: '-9px',
          left: '2px',
          backgroundColor: remoteEditor.color,
          color: 'white',
          fontSize: '9px',
          fontWeight: '700',
          padding: '0px 5px',
          borderRadius: '3px',
          whiteSpace: 'nowrap',
          zIndex: 5,
          pointerEvents: 'none'
        }}>
          {remoteEditor.name}
        </span>
      )}
      <textarea
        value={localValue}
        onChange={e => setLocalValue(e.target.value)}
        onFocus={() => onCellFocus && onCellFocus()}
        onBlur={() => { onChange(localValue); if (onCellBlur) onCellBlur(); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setLocalValue(value); e.target.blur(); }
          if (e.key === 'Tab') {
            e.preventDefault();
            onChange(localValue);
            const currentTd = e.target.closest('td');
            const currentTr = currentTd.closest('tr');
            const allTds = Array.from(currentTr.querySelectorAll('td'));
            const currentIndex = allTds.indexOf(currentTd);
            const nextTd = allTds[currentIndex + 1];
            if (nextTd) {
              const nextInput = nextTd.querySelector('input, select, textarea');
              if (nextInput) {
                setTimeout(() => {
                  nextInput.focus();
                  nextInput.click();
                }, 10);
              }
            }
          }
        }}
        rows={1}
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          fontSize: '12px',
          outline: 'none',
          padding: '2px',
          color: '#1a1a2e',
          resize: 'none',
          fontFamily: 'inherit',
          lineHeight: '1.4',
          overflow: 'hidden',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap'
        }}
        ref={el => {
          if (el) {
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
          }
        }}
        onInput={e => {
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
      />
      <span className="cell-print-value">{localValue}</span>
    </td>
  );
}

function ShowFlow({ showPresenters, rows, setRows, jobInfo, lists, notesLabel, setNotesLabel, custom2Label, setCustom2Label, custom3Label, setCustom3Label, custom4Label, setCustom4Label, columnVisibility, setColumnVisibility, columnOrder, setColumnOrder, lastSaved, viewOnly, versionNumber, setVersionNumber, currentShowId, currentUserId, socketConnected, timerEnabled, timerMode, setTimerSettings, dropdownsEnabled, setDropdownsEnabled, applyDropdownsEnabled }) {
  const [localLastSaved, setLocalLastSaved] = useState(lastSaved);

  const handleVersionChange = (val) => {
    if (setVersionNumber) setVersionNumber(val);
  };
  // Receive collaborative edits from other users
  React.useEffect(() => {
    const s = getSocket();
    if (!s || !currentShowId || viewOnly) return;

    const onCellChange = ({ rowId, field, value }) => {
      const key = `${rowId}:${field}`;
      // Don't yank text out from under someone actively typing in this cell
      if (focusedCellRef.current === key) return;
      setRows(prev => prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r)));
    };

    const onRowsChanged = ({ rows: incoming }) => {
      setRows(() => incoming);
    };

    const onCellFocus = ({ rowId, field, byId, byName }) => {
      setRemoteEditing(prev => {
        // Clear any previous cell for this user, then set the new one
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          if (v.byId !== byId) next[k] = v;
        });
        next[`${rowId}:${field}`] = { byId, byName };
        return next;
      });
    };

    const onCellBlur = ({ byId }) => {
      setRemoteEditing(prev => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          if (v.byId !== byId) next[k] = v;
        });
        return next;
      });
    };
    const onDropdownSetting = ({ dropdownsEnabled: de }) => {
      if (applyDropdownsEnabled) applyDropdownsEnabled(de);
    };
    const onLayoutChange = ({ columnOrder: co, columnVisibility: cv, labels }) => {
      if (co && setColumnOrder) setColumnOrder(co);
      if (cv && setColumnVisibility) setColumnVisibility(cv);
      if (labels) {
        if (labels.notesLabel !== undefined && setNotesLabel) setNotesLabel(labels.notesLabel);
        if (labels.custom2Label !== undefined && setCustom2Label) setCustom2Label(labels.custom2Label);
        if (labels.custom3Label !== undefined && setCustom3Label) setCustom3Label(labels.custom3Label);
        if (labels.custom4Label !== undefined && setCustom4Label) setCustom4Label(labels.custom4Label);
      }
    };

    s.on('cellChange', onCellChange);
    s.on('rowsChanged', onRowsChanged);
    s.on('cellFocus', onCellFocus);
    s.on('cellBlur', onCellBlur);
    s.on('layoutChange', onLayoutChange);
    s.on('dropdownSetting', onDropdownSetting);

    return () => {
      s.off('cellChange', onCellChange);
      s.off('rowsChanged', onRowsChanged);
      s.off('cellFocus', onCellFocus);
      s.off('cellBlur', onCellBlur);
      s.off('layoutChange', onLayoutChange);
      s.off('dropdownSetting', onDropdownSetting);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShowId, viewOnly]);

  // Broadcast helpers
  const cellBroadcastTimers = React.useRef({});

  const broadcastCell = (rowId, field, value) => {
    const s = getSocket();
    if (!s || !currentShowId || viewOnly) return;
    const key = `${rowId}:${field}`;
    if (cellBroadcastTimers.current[key]) clearTimeout(cellBroadcastTimers.current[key]);
    cellBroadcastTimers.current[key] = setTimeout(() => {
      s.emit('cellChange', { showId: currentShowId, rowId, field, value });
      delete cellBroadcastTimers.current[key];
    }, 400);
  };

  const broadcastRows = (rowsArray) => {
    const s = getSocket();
    if (s && currentShowId && !viewOnly) {
      s.emit('rowsChanged', { showId: currentShowId, rows: rowsArray });
    }
  };

  const broadcastFocus = (rowId, field) => {
    const s = getSocket();
    focusedCellRef.current = `${rowId}:${field}`;
    if (s && currentShowId && !viewOnly) {
      s.emit('cellFocus', { showId: currentShowId, rowId, field });
    }
  };
  const broadcastLayout = (payload) => {
    const s = getSocket();
    if (s && currentShowId && !viewOnly) {
      s.emit('layoutChange', { showId: currentShowId, ...payload });
    }
  };
  const broadcastBlur = () => {
    const s = getSocket();
    focusedCellRef.current = null;
    if (s && currentShowId && !viewOnly) {
      s.emit('cellBlur', { showId: currentShowId });
    }
  };
const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('cvs_token');
      const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://showflow.creativevideoservices.net';
      const visibleColumns = columns.map(col => ({ key: col.key, width: col.width }));
      const response = await fetch(`${API_URL}/api/shows/${currentShowId}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ columns: visibleColumns })
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="(.+)"/);
      a.download = match ? match[1] : 'showflow.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF generation failed. Please try again.');
    }
  };
  React.useEffect(() => {
    if (lastSaved) setLocalLastSaved(lastSaved);
  }, [lastSaved]);
  const [draggingRow, setDraggingRow] = useState(null);
  const [dragOverRow, setDragOverRow] = useState(null);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showMode, setShowMode] = useState(false);
  const [colorPickerRowId, setColorPickerRowId] = useState(null);
  const [colorPickerPos, setColorPickerPos] = useState({ dropUp: false });
  const [selectedRows, setSelectedRows] = useState([]);
  const [draggingCol, setDraggingCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  // Consistent color per user, derived from their id
  const USER_COLORS = ['#e53e3e', '#dd6b20', '#38a169', '#3182ce', '#805ad5', '#d53f8c', '#00897b', '#c05621'];
  const colorForUser = (id) => USER_COLORS[Math.abs(Number(id) || 0) % USER_COLORS.length];
  // { "rowId:field": { byId, byName } } — who is editing which cell
  const [remoteEditing, setRemoteEditing] = useState({});
  // The cell this user currently has focused, so we can ignore incoming changes to it
  const focusedCellRef = React.useRef(null);
  const toggleRowSelection = (rowId) => {
    setSelectedRows(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const duplicateSelectedRows = () => {
    if (selectedRows.length === 0) return;
    setRows(prev => {
      const selectedIndices = selectedRows.map(id => prev.findIndex(r => r.id === id));
      const maxIndex = Math.max(...selectedIndices);
      const rowsToDuplicate = prev.filter(r => selectedRows.includes(r.id));
      const duplicated = rowsToDuplicate.map(row => ({
        ...row,
        id: Date.now() + Math.random(),
        startTime: '',
      }));
      const newRows = [...prev];
      newRows.splice(maxIndex + 1, 0, ...duplicated);
      const renumbered = newRows.map((r, i) => ({ ...r, lineNumber: i }));
      const next = cascadeSlides(cascadeTimes(renumbered));
      broadcastRows(next);
      return next;
    });
    setSelectedRows([]);
  };
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.color-picker-popup') && !e.target.closest('.color-picker-btn')) {
        setColorPickerRowId(null);
      }
      if (!e.target.closest('.column-panel') && !e.target.closest('.column-panel-btn')) {
        setShowColumnPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const colorPalette = [
    ['#ffe4e6','#fecaca','#fca5a5','#f87171','#ef4444'],
    ['#ffedd5','#fed7aa','#fdba74','#fb923c','#f97316'],
    ['#fefce8','#fef08a','#fde047','#facc15','#eab308'],
    ['#dcfce7','#bbf7d0','#86efac','#4ade80','#22c55e'],
    ['#d1fae5','#a7f3d0','#6ee7b7','#34d399','#10b981'],
    ['#e0f2fe','#bae6fd','#7dd3fc','#38bdf8','#0ea5e9'],
    ['#dbeafe','#bfdbfe','#93c5fd','#60a5fa','#3b82f6'],
    ['#ede9fe','#ddd6fe','#c4b5fd','#a78bfa','#8b5cf6'],
    ['#fce7f3','#fbcfe8','#f9a8d4','#f472b6','#ec4899'],
    ['#f5f0e8','#e7d5c0','#d4b896','#c9a87c','#b8926a'],
  ];
  const [activeRow, setActiveRow] = useState(() => {
    const saved = localStorage.getItem(`cvs_active_row_${currentShowId}`);
    return saved ? parseInt(saved) : 0;
  });

  // Remember the active row per show, so it survives refreshes and tab changes
  React.useEffect(() => {
    if (currentShowId) {
      localStorage.setItem(`cvs_active_row_${currentShowId}`, activeRow);
    }
  }, [activeRow, currentShowId]);

  // Keep the active row within bounds if rows were removed
  React.useEffect(() => {
    const count = (rows || []).length;
    if (count > 0 && activeRow > count - 1) {
      setActiveRow(count - 1);
    }
  }, [rows, activeRow]);
  const [followMode, setFollowMode] = useState(true);
  const [driverName, setDriverName] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const canDrive = !viewOnly;
  const isDriver = canDrive && driverId === currentUserId;
  const [currentTime, setCurrentTime] = useState(new Date());
  // Countdown timer state
  // anchor = { rowIndex, durationSec, startedAt (ms epoch), paused, pausedAt }
  const [timerAnchor, setTimerAnchor] = useState(() => {
    try {
      const saved = localStorage.getItem(`cvs_timer_${currentShowId}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Persist the timer anchor so a refresh resumes at the right point
  React.useEffect(() => {
    if (!currentShowId) return;
    if (timerAnchor) {
      localStorage.setItem(`cvs_timer_${currentShowId}`, JSON.stringify(timerAnchor));
    } else {
      localStorage.removeItem(`cvs_timer_${currentShowId}`);
    }
  }, [timerAnchor, currentShowId]);
  const [timerNow, setTimerNow] = useState(Date.now());
  // Duration of a row in seconds (0 if blank/invalid)
  const rowDurationSec = (index) => {
    const r = (rows || [])[index];
    if (!r) return 0;
    const mins = parseInt(r.duration);
    return isNaN(mins) || mins < 0 ? 0 : mins * 60;
  };

  // Tick while the timer is running
  React.useEffect(() => {
    if (!showMode || !timerEnabled) return;
    const t = setInterval(() => setTimerNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [showMode, timerEnabled]);

  // Seconds remaining (may go negative in 'negative' mode)
  const secondsRemaining = (() => {
    if (!timerAnchor || !timerAnchor.durationSec) return null;
    const reference = timerAnchor.paused ? timerAnchor.pausedAt : timerNow;
    const elapsed = Math.floor((reference - timerAnchor.startedAt) / 1000);
    const raw = timerAnchor.durationSec - elapsed;
    // In hold mode, stop at zero
    if (timerMode === 'hold' && raw < 0) return 0;
    return raw;
  })();
  const formatCountdown = (secs) => {
    if (secs === null) return '--:--';
    const neg = secs < 0;
    const abs = Math.abs(secs);
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${neg ? '-' : ''}${m}:${s.toString().padStart(2, '0')}`;
  };

  // Broadcast a timer event as the driver
  const broadcastTimer = (payload) => {
    const s = getSocket();
    if (s && currentShowId && isDriver) {
      s.emit('timerEvent', { showId: currentShowId, ...payload });
    }
  };

  // Start the timer for a given row
  const startTimerForRow = (index, broadcast = true) => {
    const dur = rowDurationSec(index);
    const anchor = dur > 0
      ? { rowIndex: index, durationSec: dur, startedAt: Date.now(), paused: false, pausedAt: null }
      : null;
    setTimerAnchor(anchor);
    if (broadcast) broadcastTimer({ type: 'start', anchor });
  };
  // Handle reaching zero, based on the selected mode
  React.useEffect(() => {
    if (!showMode || !timerEnabled || !isDriver) return;
    if (secondsRemaining === null || timerAnchor?.paused) return;
    if (secondsRemaining > 0) return;

    if (timerMode === 'advance' && secondsRemaining <= 0) {
      const rowCount = (rows || []).length;
      const next = Math.min(timerAnchor.rowIndex + 1, rowCount - 1);
      if (next !== timerAnchor.rowIndex) {
        setActiveRow(next);
        broadcastRow(next);
        startTimerForRow(next);
      }
    }
    // 'hold' and 'negative' need no action here — display handles them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsRemaining, showMode, timerEnabled, isDriver, timerMode]);
  const togglePause = () => {
    setTimerAnchor(prev => {
      if (!prev) return prev;
      let next;
      if (prev.paused) {
        // Resume: shift startedAt forward by how long we were paused
        const pausedFor = Date.now() - prev.pausedAt;
        next = { ...prev, paused: false, pausedAt: null, startedAt: prev.startedAt + pausedFor };
      } else {
        next = { ...prev, paused: true, pausedAt: Date.now() };
      }
      broadcastTimer({ type: 'start', anchor: next });
      return next;
    });
  };
  const [timezone, setTimezone] = useState('America/Los_Angeles');
      // Receive row changes and driver updates
  React.useEffect(() => {
    const s = getSocket();
    if (!s || !showMode) return;

    const rowHandler = ({ rowIndex, driverName }) => {
      if (!followMode) return;
      setActiveRow(rowIndex);
      setDriverName(driverName);
    };
    const driverHandler = ({ driverId, driverName }) => {
      setDriverId(driverId);
      setDriverName(driverName);
    };
    const onTimerEvent = ({ type, anchor }) => {
      if (isDriver) return; // the driver is the source of truth
      if (type === 'start') setTimerAnchor(anchor);
    };

    const onTimerSettings = ({ timerEnabled: te, timerMode: tm }) => {
      if (setTimerSettings) setTimerSettings({ timerEnabled: te, timerMode: tm });
    };
    const onReconnect = () => {
      // Server state may have been reset — clear local driver belief and re-ask
      setDriverId(null);
      setDriverName(null);
      s.emit('whoIsDriving', currentShowId);
    };

    s.on('showModeRow', rowHandler);
    s.on('driverChanged', driverHandler);
    s.on('timerEvent', onTimerEvent);
    s.on('timerSettings', onTimerSettings);
    s.on('connect', onReconnect);
    s.emit('whoIsDriving', currentShowId);

    // Re-verify driver state periodically so a server restart self-corrects
    const driverPoll = setInterval(() => {
      if (s.connected) s.emit('whoIsDriving', currentShowId);
    }, 5000);

    return () => {
      clearInterval(driverPoll);
      s.off('showModeRow', rowHandler);
      s.off('driverChanged', driverHandler);
      s.off('timerEvent', onTimerEvent);
      s.off('timerSettings', onTimerSettings);
      s.off('connect', onReconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMode, followMode, currentShowId]);

    const takeControl = () => {
    const s = getSocket();
    console.log('takeControl — currentUserId:', currentUserId, 'driverId:', driverId, 'canDrive:', canDrive);
    if (s && currentShowId) s.emit('takeControl', currentShowId);
  };
  // Broadcast our row when we're the one driving
  const broadcastRow = (rowIndex) => {
    const s = getSocket();
    if (s && currentShowId && isDriver) {
      s.emit('showModeRow', { showId: currentShowId, rowIndex });
    }
  };
React.useEffect(() => {
    if (!showMode) return;
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [showMode]);

  React.useEffect(() => {
    if (!showMode) return;
    const rowCount = (rows || []).length;
       const handleKeyDown = (e) => {
      if (!isDriver) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveRow(prev => {
          const next = Math.min(prev + 1, rowCount - 1);
          broadcastRow(next);
          if (timerEnabled) startTimerForRow(next);
          return next;
        });
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveRow(prev => {
          const next = Math.max(prev - 1, 0);
          broadcastRow(next);
          if (timerEnabled) startTimerForRow(next);
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMode, rows, isDriver]);

  const formatClock = () => {
    return currentTime.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const timezones = [
    { label: 'Eastern', value: 'America/New_York' },
    { label: 'Central', value: 'America/Chicago' },
    { label: 'Mountain', value: 'America/Denver' },
    { label: 'Pacific', value: 'America/Los_Angeles' },
    { label: 'Alaska', value: 'America/Anchorage' },
    { label: 'Hawaii', value: 'Pacific/Honolulu' },
  ];
  const defaultColumnVisibility = {
    slides: true,
    remoteInPerson: true,
    mainScreens: true,
    dsm1: true,
    dsm2: true,
    secondaryScreens: true,
    notes: false,
    micAssignments: true,
    audioCues: true,
    lightingCues: true,
    stageLogistics: true,
    rowNotes: true,
    custom2: false,
    custom3: false,
    custom4: false,
  };

  const colVisibility = { ...defaultColumnVisibility, ...(columnVisibility || {}) };

  const toggleColumn = (key) => {
    const updated = { ...colVisibility, [key]: !colVisibility[key] };
    setColumnVisibility(updated);
    broadcastLayout({ columnVisibility: updated });
  };
const headerInfo = {
    jobTitle: jobInfo?.jobTitle || '',
    showDate: jobInfo?.showDates || '',
    showStartTime: jobInfo?.showStartTime || '',
    webinarId: jobInfo?.zoomWebinarId || '',
    webinarPasscode: jobInfo?.zoomWebinarPasscode || '',
    presenterZoomId: jobInfo?.zoomPresenterId || '',
    presenterZoomPasscode: jobInfo?.zoomPresenterPasscode || '',
  };

  const safeRows = rows || [];
  const presenterNames = (showPresenters || []).map(p => p.name);

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const str = timeStr.trim().toUpperCase();
    const patterns = [
      /^(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/,
      /^(\d{1,2}):(\d{2})\s*(AM|PM)$/,
      /^(\d{1,2})\s*(AM|PM)$/,
      /^(\d{1,2}):(\d{2})$/,
    ];
    let hours = null, minutes = 0;
    for (const pattern of patterns) {
      const match = str.match(pattern);
      if (match) {
        if (pattern === patterns[0]) {
          hours = parseInt(match[1]);
          minutes = parseInt(match[2]);
          const ampm = match[4];
          if (ampm === 'PM' && hours !== 12) hours += 12;
          if (ampm === 'AM' && hours === 12) hours = 0;
        } else if (pattern === patterns[1]) {
          hours = parseInt(match[1]);
          minutes = parseInt(match[2]);
          const ampm = match[3];
          if (ampm === 'PM' && hours !== 12) hours += 12;
          if (ampm === 'AM' && hours === 12) hours = 0;
        } else if (pattern === patterns[2]) {
          hours = parseInt(match[1]);
          const ampm = match[2];
          if (ampm === 'PM' && hours !== 12) hours += 12;
          if (ampm === 'AM' && hours === 12) hours = 0;
        } else if (pattern === patterns[3]) {
          hours = parseInt(match[1]);
          minutes = parseInt(match[2]);
        }
        break;
      }
    }
    if (hours === null) return null;
    return hours * 60 + minutes;
  };

  const formatTime = (totalMinutes) => {
    if (totalMinutes === null) return '';
    let mins = ((totalMinutes % 1440) + 1440) % 1440;
    const hours24 = Math.floor(mins / 60);
    const minutes = mins % 60;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };
const parseSlides = (slideStr) => {
    if (!slideStr || !slideStr.trim()) return null;
    const str = slideStr.trim();
    const rangeMatch = str.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1]);
      const end = parseInt(rangeMatch[2]);
      return { start, end, count: end - start + 1 };
    }
    const singleMatch = str.match(/^(\d+)$/);
    if (singleMatch) {
      const start = parseInt(singleMatch[1]);
      return { start, end: start, count: 1 };
    }
    return null;
  };

  const formatSlides = (start, count) => {
    if (count === 1) return `${start}`;
    return `${start} - ${start + count - 1}`;
  };

  const cascadeSlides = (updatedRows) => {
    const result = [...updatedRows];
    let lastEnd = null;
    for (let i = 0; i < result.length; i++) {
      const parsed = parseSlides(result[i].slides);
      if (parsed) {
        if (lastEnd !== null) {
          const newStart = lastEnd + 1;
          result[i] = { ...result[i], slides: formatSlides(newStart, parsed.count) };
          lastEnd = newStart + parsed.count - 1;
        } else {
          result[i] = { ...result[i], slides: formatSlides(1, parsed.count) };
          lastEnd = parsed.count;
        }
      }
    }
    return result;
  };
  const cascadeTimes = (updatedRows) => {
    const result = [...updatedRows];
    if (result.length === 0) return result;

    // Running clock, seeded from the first row's start time
    let clock = parseTime(result[0].startTime);

    for (let i = 0; i < result.length; i++) {
      const parsed = parseInt(result[i].duration);
      const hasDuration = !isNaN(parsed) && parsed >= 0;

      if (i > 0) {
        // A row gets a start time only if the clock is running AND this row has a duration
        if (clock !== null && hasDuration) {
          result[i] = { ...result[i], startTime: formatTime(clock) };
        } else {
          result[i] = { ...result[i], startTime: '' };
        }
      }

      // Advance the clock only when this row has a duration
      if (hasDuration && clock !== null) {
        clock = clock + parsed;
      }
    }

    return result;
  };

  const updateCell = (rowId, field, value) => {
    setRows(prev => {
      const updated = prev.map(row =>
        row.id === rowId ? { ...row, [field]: value } : row
      );
      // Time and slide cascades change many rows, so send the whole array
      if (field === 'startTime' || field === 'duration') {
        const cascaded = cascadeTimes(updated);
        broadcastRows(cascaded);
        return cascaded;
      }
      if (field === 'slides') {
        const cascaded = cascadeSlides(updated);
        broadcastRows(cascaded);
        return cascaded;
      }
      broadcastCell(rowId, field, value);
      return updated;
    });
  };
  const addRow = () => {
    setRows(prev => {
      const next = cascadeTimes([...prev, emptyRow(prev.length)]);
      broadcastRows(next);
      return next;
    });
  };
  
  const deleteRow = (rowId) => {
    setRows(prev => {
      const updated = prev.filter(r => r.id !== rowId);
      const renumbered = updated.map((r, i) => ({ ...r, lineNumber: i }));
      const next = cascadeSlides(cascadeTimes(renumbered));
      broadcastRows(next);
      return next;
    });
  };

  const insertRowAbove = (rowId) => {
    setRows(prev => {
      const index = prev.findIndex(r => r.id === rowId);
      const newRows = [...prev];
      newRows.splice(index, 0, emptyRow(index));
      const renumbered = newRows.map((r, i) => ({ ...r, lineNumber: i }));
      const next = cascadeSlides(cascadeTimes(renumbered));
      broadcastRows(next);
      return next;
    });
  };
  const insertAnnouncementAbove = (rowId) => {
    setRows(prev => {
      const index = prev.findIndex(r => r.id === rowId);
      const newRows = [...prev];
      newRows.splice(index, 0, emptyAnnouncement(index));
      const renumbered = newRows.map((r, i) => ({ ...r, lineNumber: i }));
      const next = cascadeTimes(renumbered);
      broadcastRows(next);
      return next;
    });
  };
  const handleRowDrop = (targetRowId) => {
    if (!draggingRow || draggingRow === targetRowId) return;
    setRows(prev => {
      const dragIndex = prev.findIndex(r => r.id === draggingRow);
      const targetIndex = prev.findIndex(r => r.id === targetRowId);
      const updated = [...prev];
      const [draggedItem] = updated.splice(dragIndex, 1);
      updated.splice(targetIndex, 0, draggedItem);
      const renumbered = updated.map((r, i) => ({ ...r, lineNumber: i }));
      const next = cascadeSlides(cascadeTimes(renumbered));
      broadcastRows(next);
      return next;
    });
    setDraggingRow(null);
    setDragOverRow(null);
  };
  const allColumns = [
    { key: 'lineNumber', label: '#', width: '36px', readOnly: true, alwaysVisible: true },
    { key: 'startTime', label: 'Start Time', width: '75px', tabIndex: 1, type: 'nowrap', alwaysVisible: true },
    { key: 'duration', label: 'Dur (min)', width: '55px', type: 'number', tabIndex: 2, alwaysVisible: true },
    { key: 'slides', label: 'Slides', width: '70px', fixedPosition: true },
    { key: 'description', label: 'Description', width: '210px', alwaysVisible: true },
    { key: 'presenter', label: 'Presenter', width: '120px', type: 'dropdown', options: presenterNames, alwaysVisible: true },
    { key: 'remoteInPerson', label: 'Remote / In Person', width: '90px', type: 'dropdown', options: (lists && lists['Remote / In Person']) || [] },
    { key: 'mainScreens', label: 'Main Screens', width: '110px', type: 'dropdown', options: (lists && lists['Main Screens']) || [] },
    { key: 'dsm1', label: 'DSM 1', width: '80px', type: 'dropdown', options: (lists && lists['DSM 1']) || [] },
    { key: 'dsm2', label: 'DSM 2', width: '80px', type: 'dropdown', options: (lists && lists['DSM 2']) || [] },
    { key: 'secondaryScreens', label: 'Secondary Screens', width: '90px', type: 'dropdown', options: (lists && lists['Secondary Screens']) || [] },
    { key: 'notes', label: '', width: '100px' },
    { key: 'custom2', label: '', width: '100px' },
    { key: 'custom3', label: '', width: '100px' },
    { key: 'custom4', label: '', width: '100px' },
    { key: 'micAssignments', label: 'Mic Assignments', width: '85px', type: 'multiline' },
    { key: 'audioCues', label: 'Audio Cues', width: '85px', type: 'dropdown', options: (lists && lists['Audio Cues']) || [] },
    { key: 'lightingCues', label: 'Lighting Cues', width: '85px', type: 'dropdown', options: (lists && lists['Lighting Cues']) || [] },
    { key: 'stageLogistics', label: 'Stage Logistics', width: '90px', type: 'dropdown', options: (lists && lists['Stage Logistics']) || [] },
    { key: 'rowNotes', label: 'Notes', width: '100px' },
  ];

  const defaultColumnOrder = allColumns.filter(col => !col.alwaysVisible && !col.fixedPosition).map(col => col.key);
  const currentColumnOrder = columnOrder || defaultColumnOrder;

  const reorderColumns = (dragKey, targetKey) => {
    const order = [...currentColumnOrder];
    const dragIndex = order.indexOf(dragKey);
    const targetIndex = order.indexOf(targetKey);
    if (dragIndex === -1 || targetIndex === -1) return;
    order.splice(dragIndex, 1);
    order.splice(targetIndex, 0, dragKey);
    setColumnOrder(order);
    broadcastLayout({ columnOrder: order });
  };

  const orderedToggleableKeys = [...currentColumnOrder];

  // When dropdowns are off, dropdown columns become plain text fields
  const applyDropdownSetting = (col) => {
    if (dropdownsEnabled === false && col.type === 'dropdown' && col.key !== 'presenter') {
      const { type, options, ...rest } = col;
      return rest;
    }
    return col;
  };

  const columns = allColumns
    .filter(col => col.alwaysVisible || (col.fixedPosition && colVisibility[col.key] !== false) || (!col.fixedPosition && !col.alwaysVisible && colVisibility[col.key] !== false))
    .sort((a, b) => {
      const aOriginal = allColumns.indexOf(a);
      const bOriginal = allColumns.indexOf(b);
      const aIsFixed = a.alwaysVisible || a.fixedPosition;
      const bIsFixed = b.alwaysVisible || b.fixedPosition;
      if (aIsFixed && bIsFixed) return aOriginal - bOriginal;
      if (aIsFixed) return -1;
      if (bIsFixed) return 1;
      const aOrder = orderedToggleableKeys.indexOf(a.key);
      const bOrder = orderedToggleableKeys.indexOf(b.key);
      if (aOrder === -1) return 1;
      if (bOrder === -1) return -1;
      return aOrder - bOrder;
    })
    .map(applyDropdownSetting);

if (viewOnly && !showMode) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* View Only Top Bar */}
        <div style={{
          backgroundColor: '#1a1a2e',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '15px', color: 'white', fontWeight: '700' }}>
              {headerInfo.jobTitle}
            </span>
            <div style={{ display: 'flex', gap: '16px' }}>
              <span style={{ fontSize: '12px', color: '#90cdf4' }}>
                <span style={{ color: '#718096' }}>Date: </span>{headerInfo.showDate}
              </span>
              <span style={{ fontSize: '12px', color: '#90cdf4' }}>
                <span style={{ color: '#718096' }}>Start: </span>{headerInfo.showStartTime}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={handleExportPDF}
              style={{
                backgroundColor: '#48bb78',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Export PDF
            </button>
            <button
              onClick={() => { setShowMode(true); setActiveRow(0); }}
              style={{
                backgroundColor: '#4299e1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Show Mode
            </button>
            <span style={{ fontSize: '12px', color: '#e53e3e', fontWeight: '600' }}>
              VIEW ONLY
            </span>
          </div>
        </div>

        {/* View Only Grid */}
        <div style={{ overflow: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#2d3748', position: 'sticky', top: 0, zIndex: 10 }}>
                {columns.map(col => (
                  <th key={col.key} style={{
                    padding: '10px 8px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#e2e8f0',
                    border: '1px solid #4a5568',
                    width: col.width,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                  }}>
                    {col.key === 'notes' ? (notesLabel || '') :
                     col.key === 'custom2' ? (custom2Label || '') :
                     col.key === 'custom3' ? (custom3Label || '') :
                     col.key === 'custom4' ? (custom4Label || '') :
                     col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safeRows.map((row, index) => {
                const rowColor = getRowColor(row, index);
                return (
                  <tr key={row.id} style={{ backgroundColor: rowColor }}>
                    {row.isAnnouncement ? (
                      <>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '700', backgroundColor: '#fdf4ff' }}>
                          {row.lineNumber}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', textAlign: 'center' }}>
                          {row.startTime}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', textAlign: 'center' }}>
                          {row.duration}
                        </td>
                        <td colSpan={columns.length - 3} style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', color: '#7c3aed', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
                          {row.announcementText}
                        </td>
                      </>
                    ) : (
                      columns.map(col => (
                        <td key={col.key} style={{
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          textAlign: 'center',
                          backgroundColor: rowColor,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {row[col.key]}
                        </td>
                      ))
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (showMode) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: '#f0f2f5',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Show Mode Top Bar */}
        <div style={{
          backgroundColor: '#1a1a2e',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '15px', color: 'white', fontWeight: '700' }}>
              {headerInfo.jobTitle}
            </span>
            <div style={{ display: 'flex', gap: '16px' }}>
              <span style={{ fontSize: '12px', color: '#90cdf4' }}>
                <span style={{ color: '#718096' }}>Date: </span>{headerInfo.showDate}
              </span>
              <span style={{ fontSize: '12px', color: '#90cdf4' }}>
                <span style={{ color: '#718096' }}>Start: </span>{headerInfo.showStartTime}
              </span>
              <span style={{ fontSize: '12px', color: '#90cdf4' }}>
                <span style={{ color: '#718096' }}>Ver: </span>{versionNumber}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#718096', letterSpacing: '1px', marginBottom: '2px' }}>TIME OF DAY</div>
              <div style={{ fontSize: '40px', fontWeight: '800', color: 'white', fontVariantNumeric: 'tabular-nums', letterSpacing: '2px', lineHeight: 1 }}>
                {formatClock()}
              </div>
            </div>

            {timerEnabled && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#718096', letterSpacing: '1px', marginBottom: '2px' }}>
                  ITEM REMAINING
                </div>
                <div
                  className={
                    secondsRemaining !== null && secondsRemaining <= 0 && timerMode === 'hold'
                      ? 'timer-flash'
                      : ''
                  }
                  style={{
                    fontSize: '40px',
                    fontWeight: '800',
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '2px',
                    lineHeight: 1,
                    color:
                      secondsRemaining === null ? '#4a5568'
                        : secondsRemaining < 0 ? '#fc8181'
                        : secondsRemaining <= 30 ? '#f6ad55'
                        : '#68d391'
                  }}
                >
                  {formatCountdown(secondsRemaining)}
                </div>
              </div>
            )}

            {timerEnabled && isDriver && (
              <button
                onClick={togglePause}
                disabled={!timerAnchor}
                style={{
                  backgroundColor: timerAnchor?.paused ? '#48bb78' : '#2d3748',
                  color: 'white',
                  border: '1px solid #4a5568',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: timerAnchor ? 'pointer' : 'not-allowed',
                  opacity: timerAnchor ? 1 : 0.4,
                  whiteSpace: 'nowrap'
                }}
              >
                {timerAnchor?.paused ? '▶ Resume' : '⏸ Pause'}
              </button>
            )}
          </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!socketConnected && (
              <span
                title="Live sync is offline. Following will not work. Try refreshing, or sign out and back in."
                style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#feb2b2',
                  backgroundColor: 'rgba(229,62,62,0.25)',
                  border: '1px solid rgba(229,62,62,0.7)',
                  borderRadius: '12px',
                  padding: '4px 12px',
                  whiteSpace: 'nowrap'
                }}
              >
                ⚠️ Live sync offline
              </span>
            )}
            <span style={{
              fontSize: '12px',
              color: isDriver ? '#f6e05e' : driverName ? '#68d391' : '#a0aec0',
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '4px 12px',
              whiteSpace: 'nowrap'
            }}>
              {isDriver ? '🎬 You are driving' : driverName ? `Following ${driverName}` : 'No one driving'}
            </span>

            {canDrive && !isDriver && (
              <button
                onClick={takeControl}
                style={{
                  backgroundColor: '#f6e05e',
                  color: '#1a1a2e',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Take Control
              </button>
            )}

            {!isDriver && (
              <button
                onClick={() => setFollowMode(prev => !prev)}
                style={{
                  backgroundColor: followMode ? '#4299e1' : '#2d3748',
                  color: 'white',
                  border: '1px solid #4a5568',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {followMode ? 'Following: ON' : 'Following: OFF'}
              </button>
            )}
            {!viewOnly && (
              <select
                value={timerEnabled ? timerMode : 'off'}
                onChange={e => {
                  const v = e.target.value;
                  const settings = v === 'off'
                    ? { timerEnabled: false }
                    : { timerEnabled: true, timerMode: v };
                  setTimerSettings(settings);
                  const s = getSocket();
                  if (s && currentShowId) {
                    s.emit('timerSettings', {
                      showId: currentShowId,
                      timerEnabled: v !== 'off',
                      timerMode: v === 'off' ? timerMode : v
                    });
                  }
                }}
                style={{
                  backgroundColor: '#2d3748',
                  color: 'white',
                  border: '1px solid #4a5568',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <option value="off">Timer: Off</option>
                <option value="advance">Timer: Auto-advance</option>
                <option value="hold">Timer: Hold at 0</option>
                <option value="negative">Timer: Count negative</option>
              </select>
            )}
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              style={{
                backgroundColor: '#2d3748',
                color: 'white',
                border: '1px solid #4a5568',
                borderRadius: '6px',
                padding: '6px 10px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowMode(false)}
              style={{
                backgroundColor: '#e53e3e',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Exit
            </button>
          </div>
        </div>

        {/* Show Mode Grid */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#2d3748', position: 'sticky', top: 0, zIndex: 10 }}>
                {columns.map(col => (
                  <th key={col.key} style={{
                    padding: '10px 8px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#e2e8f0',
                    border: '1px solid #4a5568',
                    width: col.width,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                  }}>
                    {col.key === 'notes' ? (notesLabel || '') : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safeRows.map((row, index) => {
                const rowColor = getRowColor(row, index);
                const isActive = index === activeRow;
                return (
                  <tr
                    key={row.id}
                    onClick={() => { if (isDriver) { setActiveRow(index); broadcastRow(index); } }}
                    className={isActive ? 'active-show-row' : ''}
                    style={{
                      backgroundColor: rowColor,
                      cursor: 'pointer',
                    }}
                  >
                    {row.isAnnouncement ? (
                      <>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', textAlign: 'center', fontWeight: '700', backgroundColor: '#fdf4ff' }}>
                          {row.lineNumber}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', textAlign: 'center' }}>
                          {row.startTime}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', textAlign: 'center' }}>
                          {row.duration}
                        </td>
                        <td colSpan={columns.length - 3} style={{ padding: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fdf4ff', color: '#7c3aed', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
                          {row.announcementText}
                        </td>
                      </>
                    ) : (
                      columns.map(col => (
                        <td key={col.key} style={{
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          textAlign: 'center',
                          backgroundColor: rowColor,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {row[col.key]}
                        </td>
                      ))
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <style>{`
          @keyframes timerFlash {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.15; }
          }
          .timer-flash {
            animation: timerFlash 1s ease-in-out infinite;
          }
          @keyframes rowPulse {
            0% { outline: 4px solid #e53e3e; outline-offset: -4px; }
            50% { outline: 4px solid #ffffff; outline-offset: -4px; }
            100% { outline: 4px solid #e53e3e; outline-offset: -4px; }
          }
          .active-show-row {
            animation: rowPulse 2s ease-in-out infinite !important;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="no-print">
        <div className="page-header">
          <h1>Show Flow</h1>
          <p>The interactive show flow grid. Click any cell to edit. Use dropdowns for common values.</p>
        </div>
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center', position: 'relative' }}>

          <button
            onClick={handleExportPDF}
            style={{
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Export PDF
          </button>
          <button
            className="column-panel-btn"
            onClick={() => setShowColumnPanel(prev => !prev)}
            style={{
              backgroundColor: showColumnPanel ? '#4299e1' : '#e2e8f0',
              color: showColumnPanel ? 'white' : '#1a1a2e',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ⚙️ Columns
          </button>
          <button
            onClick={duplicateSelectedRows}
            style={{
              backgroundColor: selectedRows.length > 0 ? '#4299e1' : '#e2e8f0',
              color: selectedRows.length > 0 ? 'white' : '#a0aec0',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: selectedRows.length > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            Duplicate ({selectedRows.length})
          </button>
                    <button
            onClick={() => {
              const next = !(dropdownsEnabled !== false);
              setDropdownsEnabled(next);
              const s = getSocket();
              if (s && currentShowId) {
                s.emit('dropdownSetting', { showId: currentShowId, dropdownsEnabled: next });
              }
            }}
            title={dropdownsEnabled !== false
              ? 'Dropdown lists are on. Click to switch all dropdown columns to free text.'
              : 'Dropdown lists are off. Click to restore dropdown selection.'}
            style={{
              backgroundColor: dropdownsEnabled !== false ? '#4299e1' : '#e2e8f0',
              color: dropdownsEnabled !== false ? 'white' : '#1a1a2e',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {dropdownsEnabled !== false ? '▾ Dropdowns: On' : '▾ Dropdowns: Off'}
          </button>
          <select
            value={timerEnabled ? timerMode : 'off'}
            onChange={e => {
              const v = e.target.value;
              const settings = v === 'off'
                ? { timerEnabled: false }
                : { timerEnabled: true, timerMode: v };
              setTimerSettings(settings);
              const s = getSocket();
              if (s && currentShowId) {
                s.emit('timerSettings', {
                  showId: currentShowId,
                  timerEnabled: v !== 'off',
                  timerMode: v === 'off' ? timerMode : v
                });
              }
            }}
            style={{
              backgroundColor: '#e2e8f0',
              color: '#1a1a2e',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <option value="off">⏱ Timer: Off</option>
            <option value="advance">⏱ Auto-advance</option>
            <option value="hold">⏱ Hold at 0</option>
            <option value="negative">⏱ Count negative</option>
          </select>
          <button
            onClick={() => setShowMode(prev => !prev)}
            style={{
              backgroundColor: showMode ? '#e53e3e' : '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {showMode ? 'Exit Show Mode' : 'Show Mode'}
          </button>
          <p style={{ color: '#718096', fontSize: '13px' }}>
            PDF exports landscape on 8.5 x 11
          </p>
          {localLastSaved && (
            <p style={{ color: '#68d391', fontSize: '13px' }}>
              Last saved {new Date(localLastSaved).toLocaleTimeString()}
            </p>
          )}

          {showColumnPanel && (
            <div className="column-panel" style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '16px',
              zIndex: 1000,
              minWidth: '220px',
              marginTop: '8px'
            }}>
              <p style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '12px' }}>
                Toggle Columns
              </p>
              {allColumns.filter(col => !col.alwaysVisible).map(col => (
                <div key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={colVisibility[col.key] !== false}
                    onChange={() => toggleColumn(col.key)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label style={{ fontSize: '13px', color: '#1a1a2e', cursor: 'pointer' }}
                    onClick={() => toggleColumn(col.key)}>
                    {col.key === 'notes' ? (notesLabel || 'Custom 1') :
                     col.key === 'custom2' ? (custom2Label || 'Custom 2') :
                     col.key === 'custom3' ? (custom3Label || 'Custom 3') :
                     col.key === 'custom4' ? (custom4Label || 'Custom 4') :
                     col.label}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
            
      {/* Show Flow Container */}
      <div className="card" style={{ padding: '0', overflow: 'visible' }}>

        {/* Header Rows */}
        <div style={{ backgroundColor: '#1a1a2e', padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 24px' }}>
            {[
              { label: 'Job Title', value: headerInfo.jobTitle },
              { label: 'Show Date', value: headerInfo.showDate },
              { label: 'Show Start Time', value: headerInfo.showStartTime },
              { label: 'Version #',
                value: null,
                custom: (
                  <input
                    value={versionNumber}
                    onChange={e => handleVersionChange(e.target.value)}
                    className="show-flow-version"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #4299e1',
                      color: 'white',
                      fontSize: '13px',
                      outline: 'none',
                      width: '60px',
                      padding: '2px 4px'
                    }}
                  />
                )
              },
              { label: 'Webinar', value: `ID: ${headerInfo.webinarId}   Passcode: ${headerInfo.webinarPasscode}` },
              { label: 'Presenter Zoom', value: `ID: ${headerInfo.presenterZoomId}   Passcode: ${headerInfo.presenterZoomPasscode}` },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                <span style={{ fontSize: '11px', color: '#90cdf4', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {item.label}:
                </span>
                {item.custom || (
                  <span style={{ fontSize: '13px', color: 'white', fontWeight: '500' }}>
                    {item.value}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
            tableLayout: 'fixed'
          }}>
            {/* Column Headers */}
            <thead>
              <tr style={{ backgroundColor: '#2d3748' }}>
                {columns.map(col => (
                  <th
                    key={col.key}
                    draggable={!col.alwaysVisible && !col.fixedPosition}
                    onDragStart={() => !col.alwaysVisible && !col.fixedPosition && setDraggingCol(col.key)}
                    onDragOver={e => { e.preventDefault(); !col.alwaysVisible && !col.fixedPosition && setDragOverCol(col.key); }}
                    onDrop={() => { if (draggingCol && !col.alwaysVisible && !col.fixedPosition) { reorderColumns(draggingCol, col.key); setDraggingCol(null); setDragOverCol(null); } }}
                    onDragEnd={() => { setDraggingCol(null); setDragOverCol(null); }}
                    style={{
                      padding: '8px 6px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#e2e8f0',
                      border: dragOverCol === col.key ? '2px solid #4299e1' : '1px solid #4a5568',
                      width: col.width,
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      verticalAlign: 'middle',
                      cursor: (col.alwaysVisible || col.fixedPosition) ? 'default' : 'grab',
                      opacity: draggingCol === col.key ? 0.5 : 1,
                    }}>
                    {(!col.alwaysVisible && !col.fixedPosition) && (
                      <span className="col-drag-handle" style={{ display: 'block', color: '#718096', fontSize: '10px', lineHeight: 1, marginBottom: '2px' }}>⠿</span>
                    )}
                    {['notes', 'custom2', 'custom3', 'custom4'].includes(col.key) ? (
                      <input
                        type="text"
                        value={col.key === 'notes' ? notesLabel : col.key === 'custom2' ? custom2Label : col.key === 'custom3' ? custom3Label : custom4Label}
                        onChange={e => {
                          const v = e.target.value;
                          if (col.key === 'notes') { setNotesLabel(v); broadcastLayout({ labels: { notesLabel: v } }); }
                          else if (col.key === 'custom2') { setCustom2Label(v); broadcastLayout({ labels: { custom2Label: v } }); }
                          else if (col.key === 'custom3') { setCustom3Label(v); broadcastLayout({ labels: { custom3Label: v } }); }
                          else { setCustom4Label(v); broadcastLayout({ labels: { custom4Label: v } }); }
                        }}
                        placeholder="Label..."
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid #4299e1',
                          color: '#e2e8f0',
                          fontSize: '11px',
                          fontWeight: '600',
                          outline: 'none',
                          width: '90px',
                          padding: '2px 4px',
                          textAlign: 'center'
                        }}
                      />
                    ) : col.label}
                  </th>
                ))}
                <th className="actions-col" style={{
                  padding: '8px 6px',
                  width: '60px',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: '11px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>

            {/* Rows */}
            <tbody>
              {safeRows.map((row, index) => {
                const rowColor = getRowColor(row, index);
                return (
                  <tr
                key={row.id}
                onDragOver={e => { e.preventDefault(); setDragOverRow(row.id); }}
                onDrop={() => handleRowDrop(row.id)}
                onDragEnd={() => { setDraggingRow(null); setDragOverRow(null); }}
                style={{
                  backgroundColor: rowColor,
                  opacity: draggingRow === row.id ? 0.4 : 1,
                  borderTop: dragOverRow === row.id ? '3px solid #4299e1' : 'none',
                }}
              >
                    {row.isAnnouncement ? (
                  <>
                    <td
                      draggable
                      onDragStart={() => setDraggingRow(row.id)}
                      style={{
                        padding: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#4a5568',
                        textAlign: 'center',
                        backgroundColor: '#fdf4ff',
                        width: '36px',
                        cursor: 'grab',
                        userSelect: 'none',
                        outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none',
                        outlineOffset: '-2px'
                      }}>
                      <span className="drag-handle" style={{ display: 'block', color: '#cbd5e0', fontSize: '10px', lineHeight: 1 }}>⠿</span>
                      {row.lineNumber}
                    </td>
                    <EditableCell
                      value={row.startTime}
                      type="text"
                      options={[]}
                      onChange={value => updateCell(row.id, 'startTime', value)}
                      onCellFocus={() => broadcastFocus(row.id, 'startTime')}
                      onCellBlur={broadcastBlur}
                      remoteEditor={(() => {
                        const e = remoteEditing[`${row.id}:startTime`];
                        return e ? { name: e.byName, color: colorForUser(e.byId) } : null;
                      })()}
                      style={{ backgroundColor: '#fdf4ff', width: '90px', outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none', outlineOffset: '-2px' }}
                    />
                    <EditableCell
                      value={row.duration}
                      type="number"
                      options={[]}
                      onChange={value => updateCell(row.id, 'duration', value)}
                      onCellFocus={() => broadcastFocus(row.id, 'duration')}
                      onCellBlur={broadcastBlur}
                      remoteEditor={(() => {
                        const e = remoteEditing[`${row.id}:duration`];
                        return e ? { name: e.byName, color: colorForUser(e.byId) } : null;
                      })()}
                      style={{ backgroundColor: '#fdf4ff', width: '70px', outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none', outlineOffset: '-2px' }}
                    />
                    <td
                      colSpan={columns.length - 3}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#fdf4ff',
                        position: 'relative',
                        outline: remoteEditing[`${row.id}:announcementText`]
                          ? `2px solid ${colorForUser(remoteEditing[`${row.id}:announcementText`].byId)}`
                          : selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none',
                        outlineOffset: '-2px'
                      }}
                    >
                      {remoteEditing[`${row.id}:announcementText`] && (
                        <span style={{
                          position: 'absolute',
                          top: '-9px',
                          left: '2px',
                          backgroundColor: colorForUser(remoteEditing[`${row.id}:announcementText`].byId),
                          color: 'white',
                          fontSize: '9px',
                          fontWeight: '700',
                          padding: '0px 5px',
                          borderRadius: '3px',
                          whiteSpace: 'nowrap',
                          zIndex: 5,
                          pointerEvents: 'none'
                        }}>
                          {remoteEditing[`${row.id}:announcementText`].byName}
                        </span>
                      )}
                      <input
                        type="text"
                        className="announcement-input"
                        placeholder="📢 Type crew announcement here..."
                        value={row.announcementText}
                        onChange={e => updateCell(row.id, 'announcementText', e.target.value)}
                        onFocus={() => broadcastFocus(row.id, 'announcementText')}
                        onBlur={broadcastBlur}
                        style={{
                          width: '100%',
                          border: 'none',
                          background: 'transparent',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#7c3aed',
                          outline: 'none',
                          padding: '2px'
                        }}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    {columns.map(col => {
                      if (col.readOnly) {
                        return (
                          <td
                      key={col.key}
                      draggable
                      onDragStart={() => setDraggingRow(row.id)}
                      style={{
                        padding: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#4a5568',
                        textAlign: 'center',
                        width: col.width,
                        backgroundColor: rowColor,
                        cursor: 'grab',
                        userSelect: 'none',
                        outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none',
                        outlineOffset: '-2px'
                      }}
                    >
                      <span className="drag-handle" style={{ display: 'block', color: '#cbd5e0', fontSize: '10px', lineHeight: 1 }}>⠿</span>
                      {row.lineNumber}
                    </td>
                        );
                      }
                      return (
                        <EditableCell
                          key={col.key}
                          value={row[col.key]}
                          type={col.type || 'text'}
                          options={col.options || []}
                          onChange={value => updateCell(row.id, col.key, value)}
                          onCellFocus={() => broadcastFocus(row.id, col.key)}
                          onCellBlur={broadcastBlur}
                          remoteEditor={(() => {
                            const e = remoteEditing[`${row.id}:${col.key}`];
                            return e ? { name: e.byName, color: colorForUser(e.byId) } : null;
                          })()}
                          style={{ backgroundColor: selectedRows.includes(row.id) ? '#bee3f8' : rowColor, width: col.width, outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none', outlineOffset: '-2px' }}
                        />
                      );
                    })}
                  </>
                )}

                    {/* Action buttons */}
                    <td className="actions-col" style={{
                      padding: '4px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: rowColor,
                      textAlign: 'center',
                      outline: selectedRows.includes(row.id) ? '2px solid #4299e1' : 'none',
                      outlineOffset: '-2px'
                    }}>
                      <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => toggleRowSelection(row.id)}
                          title="Select row"
                          style={{
                            background: selectedRows.includes(row.id) ? '#4299e1' : 'none',
                            border: '1px solid #e2e8f0',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: '2px 5px',
                            fontSize: '11px',
                            color: selectedRows.includes(row.id) ? 'white' : '#4a5568'
                          }}
                        >
                          ●
                        </button>
                        <button
                          onClick={() => insertRowAbove(row.id)}
                          title="Insert row above"
                          style={{
                            background: 'none',
                            border: '1px solid #e2e8f0',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: '2px 5px',
                            fontSize: '11px',
                            color: '#4299e1'
                          }}
                        >
                          ↑+
                        </button>
                        <button
                          onClick={() => insertAnnouncementAbove(row.id)}
                          title="Insert announcement above"
                          style={{
                            background: 'none',
                            border: '1px solid #e9d5ff',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: '2px 5px',
                            fontSize: '11px',
                            color: '#7c3aed'
                          }}
                        >
                          📢
                        </button>
                        <button
                          onClick={() => deleteRow(row.id)}
                          title="Delete row"
                          style={{
                            background: 'none',
                            border: '1px solid #fed7d7',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: '2px 5px',
                            fontSize: '11px',
                            color: '#e53e3e'
                          }}
                        >
                          ✕
                        </button>
                        <button
                          className="color-picker-btn"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const dropUp = spaceBelow < 300;
                            setColorPickerPos({ 
                              top: dropUp ? rect.top - 10 : rect.bottom + 5,
                              left: rect.right - 120,
                              dropUp
                            });
                            setColorPickerRowId(colorPickerRowId === row.id ? null : row.id);
                          }}
                          title="Set row color"
                          style={{
                            background: row.manualColor || 'none',
                            border: '1px solid #e2e8f0',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            padding: '2px 5px',
                            fontSize: '11px',
                            color: '#4a5568',
                            position: 'relative'
                          }}
                        >
                          🎨
                        </button>
                        {colorPickerRowId === row.id && (
                          <div className="color-picker-popup" style={{
                            position: 'fixed',
                            top: colorPickerPos.dropUp ? 'auto' : colorPickerPos.top,
                            bottom: colorPickerPos.dropUp ? window.innerHeight - colorPickerPos.top : 'auto',
                            left: colorPickerPos.left,
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            padding: '8px',
                            zIndex: 9999,
                          }}>
                            <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#4a5568' }}>Row Color</span>
                              <button
                                onClick={() => { updateCell(row.id, 'manualColor', null); setColorPickerRowId(null); }}
                                style={{ fontSize: '11px', background: 'none', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', padding: '2px 6px', color: '#718096' }}
                              >
                                Clear
                              </button>
                            </div>
                            {colorPalette.map((colorRow, rowIdx) => (
                              <div key={rowIdx} style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                                {colorRow.map((color, colIdx) => (
                                  <div
                                    key={colIdx}
                                    onClick={() => { updateCell(row.id, 'manualColor', color); setColorPickerRowId(null); }}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: color,
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      border: '1px solid rgba(0,0,0,0.1)',
                                    }}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add Row Button */}
        <div style={{ padding: '16px 16px', borderTop: '1px solid #e2e8f0',}}>
          <button
            onClick={addRow}
            style={{
              backgroundColor: '#4299e1',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + Add Row
          </button>
        </div>
      </div>
    <style>{`
        @media print {
        * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: 11in 8.5in landscape;
            margin: 0.15in;
          }
          .no-print { display: none !important; }
          body { background: white !important; }
          .header { display: none !important; }
          .page-content { 
            padding: 0 !important; 
            max-width: 100% !important;
            width: 100% !important;
          }
          .app {
            width: 100% !important;
          }
          .card {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .show-flow-header {
            font-size: 10px !important;
          }
          .show-flow-header span {
            font-size: 10px !important;
          }
          .show-flow-version {
            font-size: 14px !important;
            font-weight: 800 !important;
          }
          div[style*="overflow-x"] {
            overflow: visible !important;
            width: 100% !important;
          }
          table {
            font-size: 8px !important;
            width: 100% !important;
            table-layout: fixed !important;
            word-wrap: break-word !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            border-collapse: separate !important;
            border-spacing: 0 !important;
            border: 0.75pt solid #4a4a4a !important;
          }
          th, td {
            padding: 2px 2px !important;
            font-size: 8px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            overflow: visible !important;
            line-height: 1.4 !important;
            height: auto !important;
            max-height: none !important;
            vertical-align: top !important;

            border: 0 !important;
            border-right: 0.75pt solid #4a4a4a !important;
            border-bottom: 0.75pt solid #4a4a4a !important;
          }

          th:last-child,
          td:last-child {
            border-right: 0 !important;
          }

          tbody tr:last-child td {
            border-bottom: 0 !important;
          }

          th {
            font-size: 10px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            vertical-align: middle !important;
            text-align: center !important;
            color: black !important;
          }
          th input,
          thead th input {
            color: black !important;
            text-align: center !important;
            width: 100% !important;
            display: block !important;
          }
          th:nth-child(1), td:nth-child(1) { width: 2% !important; }
          th:nth-child(2), td:nth-child(2) { width: 7% !important; font-size: 10px !important; }
          th:nth-child(3), td:nth-child(3) { width: 4% !important; font-size: 10px !important; }
          th:nth-child(4), td:nth-child(4) { width: 9% !important; }
          th:nth-child(5), td:nth-child(5) { width: 13% !important; }
          th:nth-child(6), td:nth-child(6) { width: 7% !important; }
          th:nth-child(7), td:nth-child(7) { width: 7% !important; }
          th:nth-child(8), td:nth-child(8) { width: 6% !important; }
          th:nth-child(9), td:nth-child(9) { width: 6% !important; }
          th:nth-child(10), td:nth-child(10) { width: 7% !important; }
          th:nth-child(11), td:nth-child(11) { width: 8% !important; }
          th:nth-child(12), td:nth-child(12) { width: 9% !important; }
          th:nth-child(13), td:nth-child(13) { width: 8% !important; }
          th:nth-child(14), td:nth-child(14) { width: 7% !important; border-right: 1px solid #4a4a4a !important; }
          th:nth-child(15), td:nth-child(15) { width: 7% !important; }
          .actions-col { display: none !important; width: 0 !important; padding: 0 !important; margin: 0 !important; }
          td:nth-child(5) input, td:nth-child(5) textarea,
          td:nth-child(11) input, td:nth-child(11) textarea,
          td:nth-child(12) textarea {
            font-size: 10px !important;
          }
          th {
            font-size: 10px !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            vertical-align: middle !important;
            overflow: visible !important;
            text-align: center !important;
          }
          input, select, textarea {
            font-size: 8px !important;
            padding: 0 !important;
            border: none !important;
          }
          td > div input {
            display: none !important;
          }
          td:nth-child(2) input,
          td:nth-child(3) input {
            font-size: 10px !important;
          }
          td:nth-child(5) input, td:nth-child(5) textarea,
          td:nth-child(11) input, td:nth-child(11) textarea,
          td:nth-child(12) input, td:nth-child(12) textarea,
          td:nth-child(12) textarea {
            font-size: 10px !important;
          }
          input::placeholder, textarea::placeholder {
            color: transparent !important;
          }
          button {
            display: none !important;
          }
          span[style*="background-color: rgb(26, 26, 46)"],
          span[style*="backgroundColor"] {
            background-color: transparent !important;
            color: black !important;
            border: none !important;
            font-size: 9px !important;
          }
            .announcement-input {
            font-size: 11px !important;
          }
            .actions-col { display: none !important; width: 0 !important; padding: 0 !important; margin: 0 !important; }
            .drag-handle { display: none !important; }
            .col-drag-handle { display: none !important; }
            div[style*="background-color: rgb(26, 26, 46)"] {
            background-color: #f0f0f0 !important;
            color: black !important;
          }
          div[style*="background-color: rgb(26, 26, 46)"] {
            background-color: #bebebe !important;
            color: black !important;
          }
          div[style*="background-color: rgb(26, 26, 46)"] span,
          div[style*="background-color: rgb(26, 26, 46)"] input {
            color: black !important;
          }
          thead tr {
            background-color: #d4d4d4 !important;
          }
          thead tr th {
            background-color: #d4d4d4 !important;
            color: black !important;
          }
          tr[style*="background-color: rgb(232, 234, 237)"] {
            background-color: #d0d0d0 !important;
          }
          tr {
            height: auto !important;
            overflow: visible !important;
            page-break-inside: avoid !important;
          }
          td {
            vertical-align: middle !important;
          }
          td > div {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            height: 100% !important;
            flex-wrap: wrap !important;
          }
          .duration-input {
            display: none !important;
          }
          .duration-print-value {
            display: block !important;
            text-align: center !important;
            width: 100% !important;
            font-size: 10px !important;
          }
          td textarea {
            display: none !important;
          }
          .cell-print-value,
          .print-text-value {
            display: block !important;
            font-size: 10px !important;
            white-space: pre-wrap !important;
            word-wrap: break-word !important;
            line-height: 1.4 !important;
            text-align: center !important;
          }
          td:nth-child(5) .cell-print-value,
          td:nth-child(6) .cell-print-value,
          td:nth-child(11) .cell-print-value {
            font-size: 10px !important;
          }
      `}</style>
    </div>
  );
}

export default ShowFlow;